import React, { useEffect, useState, useCallback, Suspense } from 'react';
import {
  ExclamationCircleOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  MoreOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import 'antd/dist/antd.css';
import {
  Button,
  Col,
  ConfigProvider,
  Form,
  Modal,
  notification,
  Row,
  Table,
  Tag,
  Tooltip,
  Dropdown,
} from 'antd';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import { debouncedDataLoadWaitMs, ORDER_TYPES } from 'helpers/constants';
import trTR from 'antd/es/locale/tr_TR';
import { useHistory } from 'react-router-dom';
import userHaveRolePermission from 'helpers/userHaveRolePermission';
import { PAGE_URLS } from 'helpers/endpoints';
import { getTaskPlanList, deleteTaskPlanList } from 'store/requests/taskPlans';

let lastQuery = false;

const StoreTaskPlan = ({ route }) => {
  const history = useHistory();
  const { isViewEnabled, isCrudEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { confirm } = Modal;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedRowsFromTable, setSelectedRowsFromTable] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const Add = React.lazy(() => import('./Add'));
  const Edit = React.lazy(() => import('./Edit'));
  const [tableParams, setTableParams] = useState({
    pagination: {
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total) => `Toplam ${total} kayıt`,
      pageSizeOptions: [25, 50, 100],
      size: 'small',
      current: 1,
      pageSize: 25,
    },
  });

  const onSelectChange = (newSelectedRowKeys, newSelectedRows) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setSelectedRowsFromTable(newSelectedRows);
  };
  const rowSelection = {
    selectedRowKeys,
    selectedRowsFromTable,
    onChange: onSelectChange,
  };

  const columns = [
    {
      dataIndex: 'name',
      title: t('slotManagement.templateName'),
      sorter: true,
      showSorterTooltip: false,
    },
    {
      dataIndex: 'dateRange',
      title: t('validityDate'),
      sorter: false,
      showSorterTooltip: false,
      render: (text, record) => {
        const formatLocalDate = (unixTimestamp) => {
          const date = new Date(unixTimestamp);
          date.setHours(date.getHours() + 3);
          const day = String(date.getDate()).padStart(2, '0');
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          return `${day}/${month}/${year}`;
        };

        const formattedStartDate = record.startDate
          ? formatLocalDate(record.startDate)
          : '--';
        const formattedEndDate = record.endDate
          ? formatLocalDate(record.endDate)
          : '--';

        return `${formattedStartDate} - ${formattedEndDate}`;
      },
    },
    {
      title: t('storeType'),
      dataIndex: 'storeTypes',
      sorter: true,
      showSorterTooltip: false,
      render: (storeTypes) =>
        storeTypes && storeTypes.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
            {storeTypes.map((type) => (
              <Tag key={type.id} style={{ marginBottom: '3px' }}>
                {t(type.name)}
              </Tag>
            ))}
          </div>
        ) : (
          '--'
        ),
    },
    {
      title: t('slotManagement.status'),
      dataIndex: 'active',
      sorter: true,
      showSorterTooltip: false,
      render: (active) =>
        active ? (
          <Tag color={'red'}>{t('passive')}</Tag>
        ) : (
          <Tag color={'green'}>{t('active')}</Tag>
        ),
    },
    {
      width: 50,
      render: (dataItem) =>
        isCrudEnabled && (
          <Dropdown
            menu={{
              items: [
                {
                  label: t('edit'),
                  icon: <EditOutlined />,
                  onClick: () => {
                    setIsEditModalOpen(true);

                    setSelectedItem(dataItem);
                  },
                },
                {
                  label: t('goToDetail'),
                  icon: <ArrowRightOutlined />,
                  onClick: () => {
                    history.push({
                      pathname: '/magaza-cizelgeleme/gorev-gruplari',
                      state: { dataItem },
                    });
                  },
                },
              ],
            }}
            placement="bottomRight"
            arrow
            trigger={['click']}
            onClick={() => setSelectedItem(dataItem)}
          >
            <Button shape="circle" icon={<MoreOutlined />}></Button>
          </Dropdown>
        ),
    },
  ];

  function showDeleteTemplate() {
    confirm({
      title: t('modalDeleteTitle'),
      icon: <ExclamationCircleOutlined />,
      content: `${selectedRowsFromTable[0]?.name} ${t(
        'taskPlanDeleteConfirm'
      )}`,
      okText: t('slotManagement.delete'),
      okType: 'primary',
      cancelText: t('cancel'),
      centered: true,
      async onOk() {
        try {
          await deleteTaskPlanList(selectedRowsFromTable[0]?.id);
          notification.success({
            message: t('processCompleted'),
            description: t('taskPlanDeleted'),
          });
          lastQuery = null;
          const queryString = getQueryString();
          await loadData(queryString);
        } catch (error) {
          notification.error({
            message: t('error'),
            description: t('deleteFailed'),
          });
        }
      },
    });
  }

  const getQueryString = () => {
    let queryString = '';
    let mainTemplateSort = true;

    queryString = `${queryString}&page=${
      tableParams.pagination.current - 1
    }&size=${tableParams.pagination.pageSize}`;
    if (tableParams.column) {
      queryString = `${queryString}&sort=${tableParams.column.dataIndex},${
        ORDER_TYPES[tableParams.order]
      }`;
      mainTemplateSort = false;
    }
    if (mainTemplateSort) {
      queryString = `${queryString}&sort=main,desc`;
    }
    return queryString;
  };

  async function loadDataWithDebounce(queryString) {
    if (!loading) {
      lastQuery = queryString;
      setLoading(true);
      getTaskPlanList(queryString)
        .then((res) => {
          const { content, page } = res;
          setRows(content ? content : []);
          setSelectedRowsFromTable([]);
          setTimeout(() => {
            setSelectedRowKeys([]);
          }, 1000);
          setTableParams({
            ...tableParams,
            pagination: {
              ...tableParams.pagination,
              total: page.totalElements,
            },
          });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }

  const loadData = useCallback(
    debounce((queryString) => {
      loadDataWithDebounce(queryString);
    }, debouncedDataLoadWaitMs)
  );

  useEffect(() => {
    fetchData();
    return () => {
      lastQuery = null;
      setLoading(false);
    };
  }, [JSON.stringify(tableParams), JSON.stringify(filters)]);

  const fetchData = () => {
    const queryString = getQueryString();
    loadData(queryString);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      ...sorter,
    });
  };

  const actions = [
    {
      isCrudEnabled,
      type: 'primary',
      title: t('addTaskPlan'),
      icon: <PlusOutlined />,
      onClick: () => setIsAddModalOpen(true),
      disabled: false,
    },
    {
      isCrudEnabled: true,
      type: '',
      title: t('slotManagement.delete'),
      icon: <DeleteOutlined />,
      onClick: showDeleteTemplate,
      disabled: selectedRowKeys.length > 1 || selectedRowKeys.length < 1,
      dropdown: false,
    },
  ];

  const renderActionButtons = () => {
    return actions.map((item, idx) => {
      const button = (
        <Button
          type={item.type}
          shape="round"
          icon={item.icon}
          onClick={item.onClick}
          disabled={item.disabled}
          style={{ marginBottom: '10px', marginRight: '3px' }}
          danger={item.danger}
        />
      );

      return (
        <Tooltip title={item.title} key={idx}>
          {isCrudEnabled && item.dropdown ? (
            <Dropdown
              menu={{ items: item.dropdown.items }}
              arrow
              trigger={['click']}
              placement="bottom"
            >
              {button}
            </Dropdown>
          ) : (
            button
          )}
        </Tooltip>
      );
    });
  };

  const renderSelectedFilters = () => {
    return Object.keys(filters).map((item, idx) => {
      if (filters[item] !== null && filters[item] !== undefined) {
        return (
          <Tag
            key={idx}
            closable
            color="geekblue"
            onClose={(e) => {
              e.preventDefault();
              removeItemFromFilters(item);
            }}
          >
            {`${t(item)} : ${t(`${filters[item]}`)}`}
          </Tag>
        );
      }
    });
  };

  const removeItemFromFilters = (destinationItem) => {
    Object.keys(filters).forEach((item) => {
      if (item === destinationItem) {
        const editedFilters = {
          ...filters,
          [destinationItem]: undefined,
        };
        setFilters(editedFilters);
        form.setFieldsValue(editedFilters);
      }
    });
  };

  return (
    <div>
      <Row
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
        }}
      >
        <Col>{renderActionButtons()}</Col>
      </Row>
      <Row style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {renderSelectedFilters()}
      </Row>
      <Row style={{ marginTop: '10px' }}>
        <ConfigProvider locale={trTR}>
          <Table
            style={{ width: '100%' }}
            size="small"
            columns={columns}
            dataSource={rows}
            pagination={tableParams.pagination}
            rowSelection={rowSelection}
            loading={loading}
            onChange={handleTableChange}
            rowKey={(record) => record.id}
            scroll={{
              y: '70vh',
            }}
          />
        </ConfigProvider>
      </Row>
      <Suspense fallback={<></>}>
        {isAddModalOpen && (
          <Add
            onCloseHandler={() => setIsAddModalOpen(false)}
            onSuccessCallback={fetchData}
          />
        )}
        {isEditModalOpen && (
          <Edit
            selectedTemplate={selectedItem}
            onCloseHandler={() => setIsEditModalOpen(false)}
            onSuccessCallback={fetchData}
          />
        )}
      </Suspense>
    </div>
  );
};

export default StoreTaskPlan;
